/**
 * Paragraph → section memo for cursor lookups.
 *
 * Resolving which section the cursor is in (`_sectionAtRange`) asks Word for
 * the bookmarks of a range from the document start to the cursor — on Word for
 * Mac that lays the document out up to the caret, and it ran once a second
 * while typing (auto-save's touched section) and again on every selection
 * settle (the scope chip). Pilot report, 23 Oct 2026: Word stuck on
 * "calculating word count" with two documents and the add-in open.
 *
 * The answer only changes when the caret moves to another paragraph, so it is
 * remembered per paragraph (`Paragraph.uniqueLocalId`, WordApi 1.6). Entries
 * expire after a short while and with the document epoch (a Load replaces the
 * body), so a heading the writer moves is picked up again soon. A `null`
 * answer (a document without section bookmarks) is remembered too: that is the
 * case that re-ran the full probe on every move.
 */

import { currentDocEpoch } from "./liveDocLoadState";

const TTL_MS = 30_000;
const MAX_ENTRIES = 500;

export interface ParagraphSectionCache<T> {
  /** `{ hit: true, value }` when `id` was resolved recently in this document. */
  get(id: string | null | undefined): { hit: true; value: T } | { hit: false };
  set(id: string | null | undefined, value: T): void;
  clear(): void;
}

export function createParagraphSectionCache<T>(
  now: () => number = () => Date.now(),
  epoch: () => number = currentDocEpoch,
): ParagraphSectionCache<T> {
  const entries = new Map<string, { value: T; at: number; epoch: number }>();
  return {
    get(id) {
      if (!id) return { hit: false };
      const e = entries.get(id);
      if (!e || e.epoch !== epoch() || now() - e.at > TTL_MS) {
        if (e) entries.delete(id);
        return { hit: false };
      }
      return { hit: true, value: e.value };
    },
    set(id, value) {
      if (!id) return;
      if (entries.size >= MAX_ENTRIES) entries.clear();
      entries.set(id, { value, at: now(), epoch: epoch() });
    },
    clear() {
      entries.clear();
    },
  };
}

function uniqueLocalIdSupported(): boolean {
  try {
    return Office.context.requirements.isSetSupported("WordApi", "1.6");
  } catch {
    return false;
  }
}

/**
 * The `uniqueLocalId` of the paragraph the selection starts in — one cheap
 * round trip that reads nothing above the caret. `null` when the host lacks
 * WordApi 1.6 or the read fails, which the callers treat as a cache miss.
 */
export async function selectionParagraphId(): Promise<string | null> {
  if (typeof Word === "undefined" || !uniqueLocalIdSupported()) return null;
  try {
    return await Word.run(async (context) => {
      const para = context.document.getSelection().paragraphs.getFirstOrNullObject();
      para.load("uniqueLocalId");
      await context.sync();
      return para.isNullObject ? null : para.uniqueLocalId || null;
    });
  } catch {
    return null;
  }
}
